/**
 * Whether the HTML this bundle booted into was produced by the marketing
 * prerender (scripts/prerender-marketing.mjs stamps `data-prerendered="1"` on
 * <html>) rather than served as the empty SPA shell (dist/app-shell.html, which
 * is copied before capture and therefore unstamped).
 *
 * What this is NOT: a hydration signal. React cannot hydrate these snapshots —
 * they are captured from a live client render AFTER mount, so every value that
 * settles on mount (a decoded image's placeholder, a finished entrance
 * animation) disagrees with the client's own first render, and React 19 answers
 * any such disagreement by regenerating the tree. `createRoot` stays.
 *
 * What it IS: the knowledge that this content is already on screen. React's
 * first commit is about to replace it, so anything that would ANIMATE IN on
 * mount must render settled instead — otherwise the replacement takes a
 * headline the visitor is already reading back to opacity 0 and fades it in.
 * `arriveOnMount` (components/landing/kit/motion.ts) is the one reader.
 *
 * The flag is one-way: `markPrerenderedPaintReplaced()` clears it once React
 * has committed, so a later client-side navigation to another marketing route
 * plays its entrances normally.
 */
const PRERENDERED_ATTR = 'data-prerendered'

const bootedFromSnapshot = typeof document !== 'undefined'
  && document.documentElement.getAttribute(PRERENDERED_ATTR) === '1'

let replacingPrerenderedPaint = bootedFromSnapshot

/** True for the whole life of the page when the document came prerendered. */
export function documentWasPrerendered(): boolean {
  return bootedFromSnapshot
}

/**
 * True only for the render that replaces the prerendered paint — i.e. React's
 * first commit on a prerendered document. False everywhere else, including
 * every render on the SPA shell.
 */
export function isReplacingPrerenderedPaint(): boolean {
  return replacingPrerenderedPaint
}

/** Called once from the root layout effect, after that first commit lands. */
export function markPrerenderedPaintReplaced(): void {
  replacingPrerenderedPaint = false
}
